import {
  ProbabilityRygMetric,
  SiteMetrics,
  useHealthOverview,
  useOperationalDeploymentStats,
  useOrganisationList,
  useSite,
  useSiteList,
} from "@inrange/building-manager-api-client";
import { Tooltip } from "@inrange/theme-components";
import { formatCurrency } from "@inrange/theme-components/formatting";
import React, { useMemo, useState } from "react";
import { ButtonGroup, Spinner, ToggleButton } from "react-bootstrap";
import AvailabilityBreakdownPanel from "./AvailabilityBreakdownPanel";
import PRBreakdownPanel from "./PRBreakdownPanel";
import {
  formatNumber,
  formatPct,
  formatPctSigned,
  RYG_ICONS,
} from "./deploymentStatsUtils";
import SiteBreakdownPanel from "./SiteBreakdownPanel";

// ── Components ──────────────────────────────────────────────────────

interface StatTileProps {
  value: React.ReactNode;
  label: string;
  rygColor?: string;
  onClick?: () => void;
}

const StatTile = ({ value, label, rygColor, onClick }: StatTileProps) => {
  const icon = rygColor ? RYG_ICONS[rygColor] : undefined;
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #212529",
        borderRadius: "12px",
        padding: "16px 16px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "160px",
        width: "160px",
        flexShrink: 0,
        cursor: onClick ? "pointer" : undefined,
      }}
      onClick={onClick}
    >
      <div
        style={{
          fontSize: "1.6rem",
          fontWeight: 700,
          color: "#212529",
          lineHeight: 1.2,
          textAlign: "center",
        }}
      >
        {icon && <span style={{ fontSize: "1rem" }}>{icon} </span>}
        {value}
      </div>
      <div
        style={{
          fontSize: "1.1rem",
          color: "#6c757d",
          marginTop: "6px",
          textAlign: "center",
          lineHeight: 1.3,
        }}
      >
        {label}
      </div>
    </div>
  );
};

const RygTile = ({
  metric,
  prob,
  label,
  onClick,
}: {
  metric: ProbabilityRygMetric;
  prob: "p50" | "p90";
  label: string;
  onClick?: () => void;
}) => (
  <StatTile
    value={formatPctSigned(prob === "p50" ? metric.pctP50 : metric.pctP90)}
    label={label}
    rygColor={metric.ryg}
    onClick={onClick}
  />
);

// ── Main page ───────────────────────────────────────────────────────

type Probability = "p50" | "p90";

const OperationalDeploymentStats = () => {
  const { fetchDeploymentStats, fetchInProgressDeploymentStats } =
    useOperationalDeploymentStats();
  const healthOverview = useHealthOverview();
  useSiteList();
  useOrganisationList();
  useSite({ app: "admin", enableNonEssentialQueries: true });

  const [activeMetric, setActiveMetric] = useState<{
    key: string;
    label: string;
  } | null>(null);
  const [prob, setProb] = useState<Probability>("p50");
  const [includeConstructing, setIncludeConstructing] = useState(false);

  const data = fetchDeploymentStats.data;
  const inProgressSites = fetchInProgressDeploymentStats.sites;

  const combinedSites = useMemo(() => {
    if (!data || !includeConstructing) return data?.sites ?? [];
    return [...data.sites, ...(inProgressSites ?? [])];
  }, [includeConstructing, data, inProgressSites]);

  const combinedTotals = useMemo(() => {
    const sites = combinedSites;
    const sum = (fn: (s: SiteMetrics) => number) =>
      sites.reduce((acc, s) => acc + fn(s), 0);

    const totalGenP50 = sum((s) => s.energy?.p50?.generationMwh ?? 0);
    const totalGenP90 = sum((s) => s.energy?.p90?.generationMwh ?? 0);
    const totalDemand = sum((s) => s.energy?.p50?.demandMwh ?? 0);
    const totalOnSiteP50 = sum(
      (s) =>
        ((s.energy?.p50?.deliveredOnSitePct ?? 0) / 100) *
        (s.energy?.p50?.generationMwh ?? 0)
    );
    const totalOnSiteP90 = sum(
      (s) =>
        ((s.energy?.p90?.deliveredOnSitePct ?? 0) / 100) *
        (s.energy?.p90?.generationMwh ?? 0)
    );
    const totalBehindMeterP50 = sum(
      (s) =>
        ((s.energy?.p50?.demandFulfilledByIrPct ?? 0) / 100) *
        (s.energy?.p50?.demandMwh ?? 0)
    );
    const totalBehindMeterP90 = sum(
      (s) =>
        ((s.energy?.p90?.demandFulfilledByIrPct ?? 0) / 100) *
        (s.energy?.p90?.demandMwh ?? 0)
    );

    const rev = (
      prob: "p50" | "p90",
      field: "grossRevenue" | "arbitrage" | "licenseNetArr" | "leaseNetArr"
    ) => sum((s) => s.financials?.GBP?.[prob]?.[field] ?? 0);
    const revByModel = (
      prob: "p50" | "p90",
      field: "grossRevenue" | "arbitrage" | "licenseNetArr" | "leaseNetArr",
      model: string
    ) =>
      sites
        .filter((s) => s.investmentModel === model)
        .reduce((acc, s) => acc + (s.financials?.GBP?.[prob]?.[field] ?? 0), 0);

    const licenseSites = sites.filter(
      (s) => s.investmentModel === "license"
    ).length;
    const leaseSites = sites.filter(
      (s) => s.investmentModel === "lease"
    ).length;
    const landlordIds = new Set(
      sites
        .filter((s) => s.landlordName && !s.isOwnerOccupier)
        .map((s) => s.landlordName)
    );
    const tenantIds = new Set(
      sites.filter((s) => s.tenantName).map((s) => s.tenantName)
    );

    return {
      pvMw: sum((s) => s.pvKw ?? 0) / 1000,
      batteryMwh: sum((s) => s.batteryMwh ?? 0),
      totalGenP50,
      totalGenP90,
      totalDemand: totalDemand,
      deliveredOnSitePctP50:
        totalGenP50 > 0 ? (totalOnSiteP50 / totalGenP50) * 100 : 0,
      deliveredOnSitePctP90:
        totalGenP90 > 0 ? (totalOnSiteP90 / totalGenP90) * 100 : 0,
      demandFulfilledByIrPctP50:
        totalDemand > 0 ? (totalBehindMeterP50 / totalDemand) * 100 : 0,
      demandFulfilledByIrPctP90:
        totalDemand > 0 ? (totalBehindMeterP90 / totalDemand) * 100 : 0,
      grossRevenueP50: rev("p50", "grossRevenue"),
      grossRevenueP90: rev("p90", "grossRevenue"),
      arbitrageP50: rev("p50", "arbitrage"),
      arbitrageP90: rev("p90", "arbitrage"),
      licenseGrossRevenueP50: revByModel("p50", "grossRevenue", "license"),
      licenseGrossRevenueP90: revByModel("p90", "grossRevenue", "license"),
      leaseGrossRevenueP50: revByModel("p50", "grossRevenue", "lease"),
      leaseGrossRevenueP90: revByModel("p90", "grossRevenue", "lease"),
      licenseArbitrageP50: revByModel("p50", "arbitrage", "license"),
      licenseArbitrageP90: revByModel("p90", "arbitrage", "license"),
      leaseArbitrageP50: revByModel("p50", "arbitrage", "lease"),
      leaseArbitrageP90: revByModel("p90", "arbitrage", "lease"),
      licenseNetArrP50: revByModel("p50", "licenseNetArr", "license"),
      licenseNetArrP90: revByModel("p90", "licenseNetArr", "license"),
      leaseNetArrP90: revByModel("p90", "leaseNetArr", "lease"),
      totalNetArrP50:
        revByModel("p50", "licenseNetArr", "license") +
        revByModel("p90", "leaseNetArr", "lease"),
      totalNetArrP90:
        revByModel("p90", "licenseNetArr", "license") +
        revByModel("p90", "leaseNetArr", "lease"),
      licenseSites,
      leaseSites,
      landlordCount: landlordIds.size,
      tenantCount: tenantIds.size,
    };
  }, [combinedSites]);

  if (fetchDeploymentStats.isLoading) {
    const progress = fetchDeploymentStats.progress;
    return (
      <div className="mt-4 text-center">
        <Spinner animation="border" size="sm" /> Loading deployment stats...
        {progress && (
          <div style={{ marginTop: "12px" }}>
            <div style={{ color: "#6c757d", marginBottom: "8px" }}>
              {progress.message}
            </div>
            <div
              style={{
                width: "300px",
                height: "6px",
                backgroundColor: "#e9ecef",
                borderRadius: "3px",
                margin: "0 auto",
              }}
            >
              <div
                style={{
                  width: `${(progress.current / progress.total) * 100}%`,
                  height: "100%",
                  backgroundColor: "#212529",
                  borderRadius: "3px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (fetchDeploymentStats.isError) {
    return (
      <div className="mt-4 text-center text-danger">
        Failed to load deployment stats
      </div>
    );
  }

  if (!data) return null;

  const { currencyCode, capacity, forecastComparison, performance, health } =
    data;

  const probLabel = prob.toUpperCase();

  const ct = combinedTotals;

  const fmtCurrency = (value: number) =>
    formatCurrency(value, currencyCode, 0, false);

  const capacityLabel = includeConstructing
    ? "Operational + in progress"
    : "Operational";

  const drill = (key: string, label: string) => () =>
    setActiveMetric({ key, label });

  const rowStyle: React.CSSProperties = {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
    flexWrap: "wrap",
  };

  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#6c757d",
    margin: "8px 0 4px",
  };

  return (
    <div
      className="mt-3"
      style={{ paddingBottom: activeMetric ? "50vh" : undefined }}
    >
      {/* Toggles */}
      <div style={{ marginBottom: "12px", display: "flex", gap: "12px" }}>
        <ButtonGroup size="sm">
          <ToggleButton
            id="prob-p50"
            type="radio"
            variant={prob === "p50" ? "dark" : "outline-dark"}
            value="p50"
            checked={prob === "p50"}
            onChange={() => setProb("p50")}
          >
            P50
          </ToggleButton>
          <ToggleButton
            id="prob-p90"
            type="radio"
            variant={prob === "p90" ? "dark" : "outline-dark"}
            value="p90"
            checked={prob === "p90"}
            onChange={() => setProb("p90")}
          >
            P90
          </ToggleButton>
        </ButtonGroup>
        <ButtonGroup size="sm">
          <ToggleButton
            id="scope-operational"
            type="radio"
            variant={!includeConstructing ? "dark" : "outline-dark"}
            value="operational"
            checked={!includeConstructing}
            onChange={() => setIncludeConstructing(false)}
          >
            Operational sites
          </ToggleButton>
          <ToggleButton
            id="scope-constructing"
            type="radio"
            variant={includeConstructing ? "dark" : "outline-dark"}
            value="constructing"
            checked={includeConstructing}
            onChange={() => setIncludeConstructing(true)}
          >
            Operational + in progress sites
            {fetchInProgressDeploymentStats.isLoading && (
              <>
                {" "}
                <Spinner animation="border" size="sm" />
              </>
            )}
          </ToggleButton>
        </ButtonGroup>
      </div>

      {/* Row: Capacity & Energy */}
      <div style={rowStyle}>
        <StatTile
          value={String(capacity.siteCount)}
          label="Operational sites"
          onClick={drill("operationalSites", "Operational sites")}
        />
        <StatTile
          value={String(capacity.inProgressSiteCount)}
          label="In progress sites"
          onClick={drill("inProgressSites", "In progress sites")}
        />
        <StatTile
          value={formatNumber(ct.pvMw, 2)}
          label={`${capacityLabel} PV MW`}
          onClick={drill("pvMw", `${capacityLabel} PV MW`)}
        />
        <StatTile
          value={formatNumber(ct.batteryMwh, 2)}
          label={`${capacityLabel} battery MWh`}
          onClick={drill("batteryMwh", `${capacityLabel} battery MWh`)}
        />
        <StatTile
          value={formatNumber(
            prob === "p50" ? ct.totalGenP50 : ct.totalGenP90
          )}
          label={`Total generation MWh (${probLabel})`}
          onClick={drill("generationMwh", "Total generation MWh")}
        />
        <StatTile
          value={formatPct(
            prob === "p50"
              ? ct.deliveredOnSitePctP50
              : ct.deliveredOnSitePctP90
          )}
          label={`Delivered on-site (${probLabel})`}
          onClick={drill("deliveredOnSitePct", "Delivered on-site")}
        />
        <StatTile
          value={formatNumber(ct.totalDemand)}
          label="Total demand MWh"
          onClick={drill("demandMwh", "Total demand MWh")}
        />
        <StatTile
          value={formatPct(
            prob === "p50"
              ? ct.demandFulfilledByIrPctP50
              : ct.demandFulfilledByIrPctP90
          )}
          label={`Demand fulfilled by IR (${probLabel})`}
          onClick={drill("demandFulfilledByIrPct", "Demand fulfilled by IR")}
        />
      </div>

      {/* Row: Gross contracted annual revenue */}
      <div style={rowStyle}>
        <StatTile
          value={fmtCurrency(
            prob === "p50" ? ct.grossRevenueP50 : ct.grossRevenueP90
          )}
          label={`Gross contracted annual revenue (${probLabel})`}
          onClick={drill("grossRevenue", "Gross contracted annual revenue")}
        />
        <StatTile
          value={fmtCurrency(
            prob === "p50"
              ? ct.licenseGrossRevenueP50
              : ct.licenseGrossRevenueP90
          )}
          label={`Licence gross contracted annual revenue (${probLabel})`}
          onClick={drill(
            "licenseGrossRevenue",
            "Licence gross contracted annual revenue"
          )}
        />
        <StatTile
          value={fmtCurrency(
            prob === "p50"
              ? ct.leaseGrossRevenueP50
              : ct.leaseGrossRevenueP90
          )}
          label={`Lease gross contracted annual revenue (${probLabel})`}
          onClick={drill(
            "leaseGrossRevenue",
            "Lease gross contracted annual revenue"
          )}
        />
        <StatTile
          value={fmtCurrency(
            prob === "p50" ? ct.arbitrageP50 : ct.arbitrageP90
          )}
          label="Gross upfront revenue"
          onClick={drill("arbitrage", "Gross upfront revenue")}
        />
        <StatTile
          value={fmtCurrency(
            prob === "p50"
              ? ct.licenseArbitrageP50
              : ct.licenseArbitrageP90
          )}
          label="Licence gross upfront revenue"
          onClick={drill("licenseArbitrage", "Licence gross upfront revenue")}
        />
        <StatTile
          value={fmtCurrency(
            prob === "p50" ? ct.leaseArbitrageP50 : ct.leaseArbitrageP90
          )}
          label="Lease gross upfront revenue"
          onClick={drill("leaseArbitrage", "Lease gross upfront revenue")}
        />
      </div>

      {/* Row: Net ARR & structure */}
      <div style={rowStyle}>
        <StatTile
          value={
            <span>
              {fmtCurrency(
                prob === "p50" ? ct.totalNetArrP50 : ct.totalNetArrP90
              )}
              {prob === "p50" && (
                <>
                  {" "}
                  <Tooltip
                    text="The lease portion of Total net ARR is set manually as a P90 value, so the same lease contribution is included in P50 mode."
                    position="right"
                  >
                    <span style={{ cursor: "help" }}>*</span>
                  </Tooltip>
                </>
              )}
            </span>
          }
          label={`Total net ARR (${probLabel})`}
          onClick={drill("totalNetArr", "Total net ARR")}
        />
        <StatTile
          value={fmtCurrency(
            prob === "p50" ? ct.licenseNetArrP50 : ct.licenseNetArrP90
          )}
          label={`Licence net ARR (${probLabel})`}
          onClick={drill("licenseNetArr", "Licence net ARR")}
        />
        <StatTile
          value={
            <span>
              {fmtCurrency(ct.leaseNetArrP90)}
              {prob === "p50" && (
                <>
                  {" "}
                  <Tooltip
                    text="Lease net ARR is set manually per site as a P90 value, so the same total is shown in P50 mode."
                    position="top"
                  >
                    <span style={{ cursor: "help" }}>*</span>
                  </Tooltip>
                </>
              )}
            </span>
          }
          label={`Lease net ARR (${probLabel})`}
          onClick={drill("leaseNetArr", "Lease net ARR")}
        />
        <StatTile
          value={`${ct.licenseSites} / ${ct.leaseSites}`}
          label="Licence / lease"
          onClick={drill("licenseLease", "Licence / lease")}
        />
        <StatTile
          value={`${ct.landlordCount} / ${ct.tenantCount}`}
          label="Landlords / tenants"
          onClick={drill("landlordsTenants", "Landlords / tenants")}
        />
      </div>

      {/* Section: Performance */}
      <h2 style={sectionHeaderStyle}>Performance</h2>

      {/* Row: Forecast Comparison */}
      <div style={rowStyle}>
        <RygTile
          metric={forecastComparison.customerRevenue}
          prob={prob}
          label={`Act v. fore customer revenue (${probLabel})`}
          onClick={drill(
            "cumulativeCustomerRevenue",
            "Act v. fore customer revenue"
          )}
        />
        <RygTile
          metric={forecastComparison.generation}
          prob={prob}
          label={`Act v. fore generation (${probLabel})`}
          onClick={drill("cumulativeGenerationPct", "Act v. fore generation")}
        />
        <RygTile
          metric={forecastComparison.demandExTakeOrPay}
          prob={prob}
          label="Act v. fore demand"
          onClick={drill("cumulativeDemandPct", "Act v. fore demand")}
        />
        <RygTile
          metric={forecastComparison.onSiteExTakeOrPay}
          prob={prob}
          label={`Act v. fore on-site (${probLabel})`}
          onClick={drill("cumulativeOnSitePct", "Act v. fore on-site")}
        />
        <RygTile
          metric={forecastComparison.deliveredOnSitePct}
          prob={prob}
          label={`Act v. fore delivered on-site % (${probLabel})`}
          onClick={drill(
            "cumulativeDeliveredOnSitePctPoints",
            "Act v. fore delivered on-site %"
          )}
        />
        <RygTile
          metric={forecastComparison.export}
          prob={prob}
          label={`Act v. fore export (${probLabel})`}
          onClick={drill("cumulativeExportPct", "Act v. fore export")}
        />
        <StatTile
          value={formatPctSigned(
            forecastComparison.meteredVsSupplierExport.pct
          )}
          label="Metered vs. supplier export"
          rygColor={forecastComparison.meteredVsSupplierExport.ryg}
          onClick={drill(
            "meteredVsSupplierExport",
            "Metered vs. supplier export"
          )}
        />
      </div>

      {/* Row: Commercial Performance */}
      <div style={rowStyle}>
        <StatTile
          value={
            healthOverview.data?.aggregateAvailability?.pct != null
              ? `${healthOverview.data.aggregateAvailability.pct}%`
              : healthOverview.isLoading
                ? "Loading..."
                : "N/A"
          }
          label="Aggregate PV availability"
          rygColor={healthOverview.data?.aggregateAvailability?.ryg}
          onClick={drill("aggregateAvailability", "Aggregate PV availability")}
        />
        <StatTile
          value={String(performance.red)}
          label="Commercial perf. issues"
          rygColor="red"
          onClick={drill("performanceRed", "Commercial performance - red")}
        />
        <StatTile
          value={String(performance.amber)}
          label="Commercial perf. issues"
          rygColor="amber"
          onClick={drill("performanceAmber", "Commercial performance - amber")}
        />
        <StatTile
          value={String(performance.green)}
          label="Performing as expected"
          rygColor="green"
          onClick={drill("performanceGreen", "Commercial performance - green")}
        />
      </div>

      {/* Row: Aggregate GPR + GPR breakdown */}
      <div style={rowStyle}>
        <StatTile
          value={
            healthOverview.data?.aggregatePR?.last12Months?.pct != null
              ? `${healthOverview.data.aggregatePR.last12Months.pct}%`
              : healthOverview.isLoading
                ? "Loading..."
                : "N/A"
          }
          label="Aggregate GPR"
          rygColor={healthOverview.data?.aggregatePR?.last12Months?.ryg ?? undefined}
          onClick={drill("aggregatePR", "Aggregate GPR")}
        />
        {(() => {
          if (healthOverview.isLoading) {
            return (
              <>
                <StatTile value="Loading..." label="GPR unhealthy" rygColor="red" />
                <StatTile value="Loading..." label="GPR unhealthy" rygColor="amber" />
                <StatTile value="Loading..." label="GPR healthy" rygColor="green" />
              </>
            );
          }
          const prSites = healthOverview.data?.allSitePR ?? [];
          const gprRed = prSites.filter((s) => s.last12Months?.ryg === "red").length;
          const gprAmber = prSites.filter((s) => s.last12Months?.ryg === "amber").length;
          const gprGreen = prSites.filter((s) => s.last12Months?.ryg === "green" || !s.last12Months?.ryg).length;
          return (
            <>
              <StatTile
                value={String(gprRed)}
                label="GPR unhealthy"
                rygColor="red"
                onClick={drill("gprRed", `GPR - red (${gprRed} site${gprRed !== 1 ? "s" : ""})`)}
              />
              <StatTile
                value={String(gprAmber)}
                label="GPR unhealthy"
                rygColor="amber"
                onClick={drill("gprAmber", `GPR - amber (${gprAmber} site${gprAmber !== 1 ? "s" : ""})`)}
              />
              <StatTile
                value={String(gprGreen)}
                label="GPR healthy"
                rygColor="green"
                onClick={drill("gprGreen", `GPR - green (${gprGreen} site${gprGreen !== 1 ? "s" : ""})`)}
              />
            </>
          );
        })()}
      </div>

      {/* Section: Health */}
      <h2 style={sectionHeaderStyle}>Health</h2>

      {/* Row: Aggregate OPR + OPR breakdown */}
      <div style={rowStyle}>
        <StatTile
          value={
            healthOverview.data?.aggregateOPR?.last12Months?.pct != null
              ? `${healthOverview.data.aggregateOPR.last12Months.pct}%`
              : healthOverview.isLoading
                ? "Loading..."
                : "N/A"
          }
          label="Aggregate OPR"
          rygColor={healthOverview.data?.aggregateOPR?.last12Months?.ryg ?? undefined}
          onClick={drill("aggregateOPR", "Aggregate OPR")}
        />
        {(() => {
          if (healthOverview.isLoading) {
            return (
              <>
                <StatTile value="Loading..." label="OPR unhealthy" rygColor="red" />
                <StatTile value="Loading..." label="OPR unhealthy" rygColor="amber" />
                <StatTile value="Loading..." label="OPR healthy" rygColor="green" />
              </>
            );
          }
          const prSites = healthOverview.data?.allSitePR ?? [];
          const oprRed = prSites.filter((s) => s.oprLast12Months?.ryg === "red").length;
          const oprAmber = prSites.filter((s) => s.oprLast12Months?.ryg === "amber").length;
          const oprGreen = prSites.filter((s) => s.oprLast12Months?.ryg === "green" || !s.oprLast12Months?.ryg).length;
          return (
            <>
              <StatTile
                value={String(oprRed)}
                label="OPR unhealthy"
                rygColor="red"
                onClick={drill("oprRed", `OPR - red (${oprRed} site${oprRed !== 1 ? "s" : ""})`)}
              />
              <StatTile
                value={String(oprAmber)}
                label="OPR unhealthy"
                rygColor="amber"
                onClick={drill("oprAmber", `OPR - amber (${oprAmber} site${oprAmber !== 1 ? "s" : ""})`)}
              />
              <StatTile
                value={String(oprGreen)}
                label="OPR healthy"
                rygColor="green"
                onClick={drill("oprGreen", `OPR - green (${oprGreen} site${oprGreen !== 1 ? "s" : ""})`)}
              />
            </>
          );
        })()}
      </div>

      {/* Row: Health (split into current vs last-month-analysis dimensions) */}
      <div style={rowStyle}>
        <StatTile
          value={
            health.currentIssues !== null ? String(health.currentIssues) : "N/A"
          }
          label="Current health issues"
          rygColor={
            health.currentIssues !== null && health.currentIssues > 0
              ? "red"
              : undefined
          }
          onClick={
            health.currentIssues !== null
              ? drill("currentIssues", "Current health issues")
              : undefined
          }
        />
        <StatTile
          value={
            health.currentlyHealthy !== null
              ? String(health.currentlyHealthy)
              : "N/A"
          }
          label="Currently healthy"
          rygColor={health.currentlyHealthy !== null ? "green" : undefined}
          onClick={
            health.currentlyHealthy !== null
              ? drill("currentlyHealthy", "Currently healthy")
              : undefined
          }
        />
        <StatTile
          value={
            health.lastMonthIssues !== null
              ? String(health.lastMonthIssues)
              : "N/A"
          }
          label="Last month analysis health issues"
          rygColor={
            health.lastMonthIssues !== null && health.lastMonthIssues > 0
              ? "red"
              : undefined
          }
          onClick={
            health.lastMonthIssues !== null
              ? drill(
                  "lastMonthIssues",
                  "Last month analysis health issues"
                )
              : undefined
          }
        />
        <StatTile
          value={
            health.lastMonthHealthy !== null
              ? String(health.lastMonthHealthy)
              : "N/A"
          }
          label="Last month analysis healthy"
          rygColor={health.lastMonthHealthy !== null ? "green" : undefined}
          onClick={
            health.lastMonthHealthy !== null
              ? drill(
                  "lastMonthHealthy",
                  "Last month analysis healthy"
                )
              : undefined
          }
        />
      </div>

      {activeMetric &&
        activeMetric.key !== "aggregateAvailability" &&
        activeMetric.key !== "aggregatePR" &&
        activeMetric.key !== "aggregateOPR" &&
        !activeMetric.key.startsWith("gpr") &&
        !activeMetric.key.startsWith("opr") && (
          <SiteBreakdownPanel
            key={activeMetric.key}
            metricKey={activeMetric.key}
            sites={
              activeMetric.key === "operationalSites"
                ? data.sites
                : combinedSites
            }
            inProgressSites={inProgressSites ?? undefined}
            currencyCode={currencyCode}
            onClose={() => setActiveMetric(null)}
            tileLabel={activeMetric.label}
          />
        )}
      {activeMetric?.key === "aggregateAvailability" && healthOverview.data && (
        <AvailabilityBreakdownPanel
          sites={healthOverview.data.allSiteAvailability}
          aggregatePct={healthOverview.data.aggregateAvailability?.pct ?? null}
          aggregateRyg={healthOverview.data.aggregateAvailability?.ryg ?? "green"}
          onClose={() => setActiveMetric(null)}
        />
      )}
      {activeMetric?.key === "aggregatePR" &&
        healthOverview.data?.aggregatePR &&
        healthOverview.data?.allSitePR && (
          <PRBreakdownPanel
            sites={healthOverview.data.allSitePR}
            aggregatePR={healthOverview.data.aggregatePR}
            onClose={() => setActiveMetric(null)}
          />
        )}
      {activeMetric?.key === "aggregateOPR" &&
        healthOverview.data?.aggregateOPR &&
        healthOverview.data?.allSitePR && (
          <PRBreakdownPanel
            sites={healthOverview.data.allSitePR}
            aggregatePR={healthOverview.data.aggregateOPR}
            onClose={() => setActiveMetric(null)}
            mode="opr"
          />
        )}
      {activeMetric?.key.startsWith("gpr") &&
        healthOverview.data?.aggregatePR &&
        healthOverview.data?.allSitePR && (
          <PRBreakdownPanel
            sites={healthOverview.data.allSitePR.filter((s) => {
              const ryg = s.last12Months?.ryg;
              if (activeMetric.key === "gprRed") return ryg === "red";
              if (activeMetric.key === "gprAmber") return ryg === "amber";
              return ryg === "green" || !ryg;
            })}
            aggregatePR={healthOverview.data.aggregatePR}
            onClose={() => setActiveMetric(null)}
            title={activeMetric.label}
          />
        )}
      {activeMetric?.key.startsWith("opr") &&
        activeMetric.key !== "aggregateOPR" &&
        healthOverview.data?.aggregateOPR &&
        healthOverview.data?.allSitePR && (
          <PRBreakdownPanel
            sites={healthOverview.data.allSitePR.filter((s) => {
              const ryg = s.oprLast12Months?.ryg;
              if (activeMetric.key === "oprRed") return ryg === "red";
              if (activeMetric.key === "oprAmber") return ryg === "amber";
              return ryg === "green" || !ryg;
            })}
            aggregatePR={healthOverview.data.aggregateOPR}
            onClose={() => setActiveMetric(null)}
            title={activeMetric.label}
            mode="opr"
          />
        )}
    </div>
  );
};

export default OperationalDeploymentStats;
